import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  useMutation,
  useQuery
} from '@apollo/client';
import { Query, Mutation, RunEvalInput, RunMultiModelEvalInput, EvalTestCaseInput, EvolveAgentPromptInput } from '../types/generated/graphql';

// ---------- OpenRouter model catalogue + agents ----------
const QUERY_LIST_OPENROUTER_MODELS = gql`
  query listOpenRouterModels {
    listOpenRouterModels {
      id
      name
      provider
      inputPer1M
      outputPer1M
      contextLength
    }
  }
`;

const QUERY_LIST_EVAL_AGENTS = gql`
  query listEvalAgents {
    listEvalAgents {
      id
      specialization
      version
      name
      type
      scoreDirection
      min_age
      max_age
      description
      thumbnails {
        url
        width
        height
      }
      dataRequirement
      summarizationNeed
      triggers {
        always
        categoryTags
        riskFlags
      }
    }
  }
`;

const QUERY_LIST_AGENT_PROMPTS = gql`
  query listAgentPrompts($kind: AgentPromptKind, $id: String) {
    listAgentPrompts(kind: $kind, id: $id) {
      kind
      id
      version
      body
      displayName
      origin
      parentVersion
      lastEvalScore
      createdAt
      updatedAt
    }
  }
`;

const MUTATION_SEED_TEST_CASES = gql`
  mutation seedEvalTestCasesFromScores($count: Int!) {
    seedEvalTestCasesFromScores(count: $count) {
      created
      skipped
      testCaseIds
    }
  }
`;

const MUTATION_SAVE_AGENT_PROMPT = gql`
  mutation saveAgentPrompt($input: SaveAgentPromptInput!) {
    saveAgentPrompt(input: $input) {
      kind
      id
      version
      body
      displayName
      origin
      parentVersion
      lastEvalScore
      createdAt
      updatedAt
    }
  }
`;

const MUTATION_FORK_AGENT_PROMPT = gql`
  mutation forkAgentPrompt(
    $kind: AgentPromptKind!
    $id: String!
    $fromVersion: String!
    $newVersion: String
    $displayName: String
  ) {
    forkAgentPrompt(
      kind: $kind
      id: $id
      fromVersion: $fromVersion
      newVersion: $newVersion
      displayName: $displayName
    ) {
      kind
      id
      version
      body
      displayName
      origin
      parentVersion
      lastEvalScore
      createdAt
      updatedAt
    }
  }
`;

const MUTATION_DELETE_AGENT_PROMPT = gql`
  mutation deleteAgentPrompt($kind: AgentPromptKind!, $id: String!, $version: String!) {
    deleteAgentPrompt(kind: $kind, id: $id, version: $version)
  }
`;

const QUERY_GET_EVAL_REPORTS_FOR_AGENT = gql`
  query getEvalReportsForAgent(
    $agentSpec: AgentSpecialization!
    $agentVersion: String
    $promptVersion: String
    $model: String
    $limit: Int
  ) {
    getEvalReportsForAgent(
      agentSpec: $agentSpec
      agentVersion: $agentVersion
      promptVersion: $promptVersion
      model: $model
      limit: $limit
    ) {
      id
      agentSpec
      agentVersion
      promptVersion
      model
      totalCases
      passedCases
      overallScore
      totalTokenUsage { input output total }
      totalCost
      ranAt
    }
  }
`;

export const useListOpenRouterModels = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'listOpenRouterModels'>;
  return useQuery<T>(QUERY_LIST_OPENROUTER_MODELS, {
    client,
    fetchPolicy: 'cache-first'
  });
};

export const useListEvalAgents = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'listEvalAgents'>;
  return useQuery<T>(QUERY_LIST_EVAL_AGENTS, {
    client,
    fetchPolicy: 'cache-first'
  });
};

export const useListAgentPrompts = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'listAgentPrompts'>;
  return useQuery<T>(QUERY_LIST_AGENT_PROMPTS, {
    client,
    fetchPolicy: 'cache-and-network'
  });
};

export const useSeedEvalTestCases = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'seedEvalTestCasesFromScores'>;
  return useMutation<T, { count: number }>(MUTATION_SEED_TEST_CASES, {
    client,
    refetchQueries: ['getEvalTestCases']
  });
};

export const useSaveAgentPrompt = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'saveAgentPrompt'>;
  return useMutation<T, { input: { kind: string; id: string; body: string; version?: string; displayName?: string; parentVersion?: string; origin?: string } }>(
    MUTATION_SAVE_AGENT_PROMPT,
    { client, refetchQueries: ['listAgentPrompts'] }
  );
};

export const useForkAgentPrompt = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'forkAgentPrompt'>;
  return useMutation<T, { kind: string; id: string; fromVersion: string; newVersion?: string; displayName?: string }>(
    MUTATION_FORK_AGENT_PROMPT,
    { client, refetchQueries: ['listAgentPrompts'] }
  );
};

export const useDeleteAgentPrompt = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'deleteAgentPrompt'>;
  return useMutation<T, { kind: string; id: string; version: string }>(
    MUTATION_DELETE_AGENT_PROMPT,
    { client, refetchQueries: ['listAgentPrompts'] }
  );
};

export const useGetEvalReportsForAgent = (
  client: ApolloClient<NormalizedCacheObject>,
  params: { agentSpec?: string; agentVersion?: string; promptVersion?: string; model?: string; limit?: number }
) => {
  type T = Pick<Query, 'getEvalReportsForAgent'>;
  return useQuery<T>(QUERY_GET_EVAL_REPORTS_FOR_AGENT, {
    client,
    variables: {
      agentSpec: params.agentSpec,
      agentVersion: params.agentVersion ?? null,
      promptVersion: params.promptVersion ?? null,
      model: params.model ?? null,
      limit: params.limit ?? 200
    },
    skip: !params.agentSpec,
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });
};

// ---------- Test cases ----------
const QUERY_GET_EVAL_TEST_CASES = gql`
  query getEvalTestCases($limit: Int) {
    getEvalTestCases(limit: $limit) {
      id
      videoId
      note
      expected
      addedBy
      createdAt
      updatedAt
    }
  }
`;

const MUTATION_ADD_EVAL_TEST_CASE = gql`
  mutation addEvalTestCase($input: EvalTestCaseInput!) {
    addEvalTestCase(input: $input) {
      id
      videoId
      expected
      createdAt
      updatedAt
    }
  }
`;

const MUTATION_DELETE_EVAL_TEST_CASE = gql`
  mutation deleteEvalTestCase($id: ID!) {
    deleteEvalTestCase(id: $id)
  }
`;

export const useGetEvalTestCases = (client: ApolloClient<NormalizedCacheObject>, limit = 100) => {
  type T = Pick<Query, 'getEvalTestCases'>;
  return useQuery<T>(QUERY_GET_EVAL_TEST_CASES, {
    client,
    variables: { limit },
    fetchPolicy: 'cache-and-network'
  });
};

export const useAddEvalTestCase = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'addEvalTestCase'>;
  return useMutation<T, { input: EvalTestCaseInput }>(MUTATION_ADD_EVAL_TEST_CASE, {
    client,
    refetchQueries: ['getEvalTestCases']
  });
};

export const useDeleteEvalTestCase = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'deleteEvalTestCase'>;
  return useMutation<T, { id: string }>(MUTATION_DELETE_EVAL_TEST_CASE, {
    client,
    refetchQueries: ['getEvalTestCases']
  });
};

// ---------- Reports ----------
const QUERY_GET_EVAL_REPORTS = gql`
  query getEvalReports($limit: Int) {
    getEvalReports(limit: $limit) {
      id
      agentSpec
      agentVersion
      promptVersion
      model
      totalCases
      passedCases
      overallScore
      totalTokenUsage {
        input
        output
        total
      }
      totalCost
      ranAt
      fieldAccuracy {
        score {
          avgDistance
          avgContribution
        }
        rating {
          correct
          total
        }
        restricted {
          correct
          total
        }
      }
      error
    }
  }
`;

const QUERY_GET_EVAL_REPORT = gql`
  query getEvalReport($id: ID!) {
    getEvalReport(id: $id) {
      id
      agentSpec
      agentVersion
      promptVersion
      model
      totalCases
      passedCases
      overallScore
      results
      totalTokenUsage {
        input
        output
        total
      }
      totalCost
      ranAt
      fieldAccuracy {
        score {
          avgDistance
          avgContribution
        }
        rating {
          correct
          total
        }
        restricted {
          correct
          total
        }
      }
      error
    }
  }
`;

const MUTATION_RUN_EVAL = gql`
  mutation runEval($input: RunEvalInput!) {
    runEval(input: $input) {
      ok
      messageType
      jobId
    }
  }
`;

const MUTATION_RUN_MULTI_MODEL_EVAL = gql`
  mutation runMultiModelEval($input: RunMultiModelEvalInput!) {
    runMultiModelEval(input: $input) {
      ok
      messageType
      jobId
    }
  }
`;

const MUTATION_DELETE_EVAL_REPORT = gql`
  mutation deleteEvalReport($id: ID!) {
    deleteEvalReport(id: $id)
  }
`;

export const useGetEvalReports = (client: ApolloClient<NormalizedCacheObject>, limit = 50) => {
  type T = Pick<Query, 'getEvalReports'>;
  return useQuery<T>(QUERY_GET_EVAL_REPORTS, {
    client,
    variables: { limit },
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });
};

export const useGetEvalReport = (client: ApolloClient<NormalizedCacheObject>, id: string | null) => {
  type T = Pick<Query, 'getEvalReport'>;
  return useQuery<T>(QUERY_GET_EVAL_REPORT, {
    client,
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network'
  });
};

export const useRunEval = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'runEval'>;
  return useMutation<T, { input: RunEvalInput }>(MUTATION_RUN_EVAL, {
    client,
    refetchQueries: ['getEvalReports']
  });
};

export const useRunMultiModelEval = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'runMultiModelEval'>;
  return useMutation<T, { input: RunMultiModelEvalInput }>(MUTATION_RUN_MULTI_MODEL_EVAL, {
    client,
    refetchQueries: ['getEvalReports']
  });
};

export const useDeleteEvalReport = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'deleteEvalReport'>;
  return useMutation<T, { id: string }>(MUTATION_DELETE_EVAL_REPORT, {
    client,
    refetchQueries: ['getEvalReports']
  });
};

const MUTATION_EVOLVE_AGENT_PROMPT = gql`
  mutation evolveAgentPrompt($input: EvolveAgentPromptInput!) {
    evolveAgentPrompt(input: $input) {
      ok
      messageType
      jobId
    }
  }
`;

export const useEvolveAgentPrompt = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'evolveAgentPrompt'>;
  return useMutation<T, { input: EvolveAgentPromptInput }>(MUTATION_EVOLVE_AGENT_PROMPT, {
    client,
    refetchQueries: ['listAgentPrompts']
  });
};
